const STORAGE_KEY = "adatis.professionalRosterUpdatedAt";
const UPDATED_EVENT = "adatis:professional-roster-updated";

export type RosterUpdateSource = "same-tab" | "other-tab";
export type RosterUpdateKind = "routine" | "services";

type RosterUpdateDetail = {
  name?: string;
  kind?: RosterUpdateKind;
  professionalId?: number;
};

export type RosterUpdateNotice = {
  key: string;
  name?: string;
};

const isKind = (value: unknown): value is RosterUpdateKind =>
  value === "routine" || value === "services";

const readStoredUpdate = (raw: string | null): RosterUpdateDetail => {
  if (!raw) {
    return {};
  }

  try {
    const parsed = JSON.parse(raw) as {
      name?: unknown;
      kind?: unknown;
      professionalId?: unknown;
    };

    return {
      name: typeof parsed.name === "string" ? parsed.name.trim() : "",
      kind: isKind(parsed.kind) ? parsed.kind : undefined,
      professionalId:
        typeof parsed.professionalId === "number" && parsed.professionalId > 0
          ? parsed.professionalId
          : undefined,
    };
  } catch {
    return {};
  }
};

/** i18n key for the other-tab notice. Routine and services use different sentences. */
export const rosterUpdateNotice = (
  name: string,
  kind?: RosterUpdateKind
): RosterUpdateNotice => {
  const trimmed = name.trim();

  if (kind === "services") {
    return trimmed
      ? { key: "studio.professionals.specialty.updatedElsewhereNamed", name: trimmed }
      : { key: "studio.professionals.specialty.updatedElsewhere" };
  }

  if (kind === "routine") {
    return trimmed
      ? { key: "studio.professionals.routine.updatedElsewhereRoutineNamed", name: trimmed }
      : { key: "studio.professionals.routine.updatedElsewhereRoutine" };
  }

  return trimmed
    ? { key: "studio.professionals.routine.updatedElsewhereNamed", name: trimmed }
    : { key: "studio.professionals.routine.updatedElsewhere" };
};

/** Path that opens the person's profile. Services land on the checklist. */
export const rosterProfilePath = (
  professionalId: number,
  kind?: RosterUpdateKind
): string => {
  const params = new URLSearchParams({
    open: String(professionalId),
    tab: kind === "services" ? "enablement" : "data",
  });

  return `/professionals?${params.toString()}`;
};

/** Path that opens the weekly hours modal for the person. */
export const rosterSchedulePath = (professionalId: number): string =>
  `/professionals?schedule=${professionalId}`;

/** Tell other open tabs (and this page, if it is listening) who changed, and whether it was the routine or the services. */
export const markProfessionalRosterUpdated = (
  name: string,
  kind: RosterUpdateKind,
  professionalId: number
): void => {
  const trimmed = name.trim();

  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ at: Date.now(), name: trimmed, kind, professionalId })
    );
  } catch {
    // Private mode can block storage; the same-tab event still runs.
  }

  window.dispatchEvent(
    new CustomEvent<RosterUpdateDetail>(UPDATED_EVENT, {
      detail: { name: trimmed, kind, professionalId },
    })
  );
};

export const subscribeProfessionalRosterUpdates = (
  onUpdate: (
    source: RosterUpdateSource,
    name: string,
    kind?: RosterUpdateKind,
    professionalId?: number
  ) => void
): (() => void) => {
  const onSameTab = (event: Event) => {
    const detail = (event as CustomEvent<RosterUpdateDetail>).detail;

    onUpdate("same-tab", detail?.name?.trim() ?? "", detail?.kind, detail?.professionalId);
  };

  const onStorage = (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY) {
      return;
    }

    const stored = readStoredUpdate(event.newValue);

    onUpdate("other-tab", stored.name ?? "", stored.kind, stored.professionalId);
  };

  window.addEventListener(UPDATED_EVENT, onSameTab);
  window.addEventListener("storage", onStorage);

  return () => {
    window.removeEventListener(UPDATED_EVENT, onSameTab);
    window.removeEventListener("storage", onStorage);
  };
};
